import api from './api';
import { API_PATHS } from './apiEndpoints';

/**
 * Regional Data Service
 * Service to fetch regional breakdown data by campaign and month
 */
class RegionalDataService {
  /**
   * Get campaign regional breakdown data
   * @param {Object} filters - Filter parameters
   * @param {string} filters.brand - Brand filter
   * @param {string} filters.year - Year filter (e.g., '2025')
   * @param {string} filters.campaign - Campaign filter
   * @returns {Promise} API response
   */
  async getCampaignRegionalBreakdown(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.brand && filters.brand !== 'all') {
        params.append('brand', filters.brand);
      }
      
      if (filters.year) {
        params.append('year', filters.year);
      }
      
      if (filters.campaign && filters.campaign !== 'all') {
        params.append('campaign', filters.campaign);
      }
      
      const queryString = params.toString();
      const url = `${API_PATHS.CAMPAIGN_REGIONAL_BREAKDOWN}${queryString ? `?${queryString}` : ''}`;
      
      console.log('🌏 Fetching regional breakdown data:', url);
      
      const response = await api.get(url);
      
      console.log('✅ Regional breakdown data received:', {
        dataCount: response.data?.data?.length || 0,
        summary: response.data?.summary
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching regional breakdown data:', error);
      throw error;
    }
  }
  
  /**
   * Process regional data for RegionBrandMonthlyTable component
   * @param {Array} rawData - Raw data from API
   * @returns {Object} Processed data with regions and months
   */
  processRegionalData(rawData) {
    if (!rawData || !Array.isArray(rawData)) {
      return { regions: [], months: [] };
    }
    
    // Extract unique months and sort them
    const monthsSet = new Set();
    rawData.forEach(item => {
      if (item.month) {
        monthsSet.add(item.month);
      }
    });
    const months = Array.from(monthsSet).sort();
    
    // Group data by region and brand
    const regionMap = new Map();
    
    rawData.forEach(item => {
      const region = item.region || 'Unknown Region';
      const brand = item.brand_name || 'Unknown Brand';
      const campaign = item.campaign_name || 'Unknown Campaign';
      const month = item.month || 'Unknown';
      
      if (!regionMap.has(region)) {
        regionMap.set(region, {
          region,
          brands: new Map(),
          totals: {}
        });
      }
      
      const regionData = regionMap.get(region);
      
      if (!regionData.brands.has(brand)) {
        regionData.brands.set(brand, {
          brand,
          campaigns: new Map(),
          totals: {}
        });
      }
      
      const brandData = regionData.brands.get(brand);
      
      if (!brandData.campaigns.has(campaign)) {
        brandData.campaigns.set(campaign, {
          campaign,
          monthlyData: new Map()
        });
      }
      
      const campaignData = brandData.campaigns.get(campaign);
      
      // Add monthly data
      campaignData.monthlyData.set(month, {
        budget: parseFloat(item.given_cost || 0),
        planning: parseFloat(item.planning_cost || 0),
        actual: parseFloat(item.actual_cost || 0)
      });
    });
    
    // Convert map to array format expected by component
    const regions = Array.from(regionMap.entries()).map(([regionName, regionData]) => {
      const brands = Array.from(regionData.brands.entries()).map(([brandName, brandData]) => {
        const campaigns = Array.from(brandData.campaigns.entries()).map(([campaignName, campaignData]) => {
          const monthlyData = {};
          months.forEach(month => {
            const data = campaignData.monthlyData.get(month) || { budget: 0, planning: 0, actual: 0 };
            monthlyData[month] = data;
          });
          
          return {
            campaign: campaignName,
            monthlyData
          };
        });
        
        return {
          brand: brandName,
          campaigns
        };
      });
      
      return {
        region: regionName,
        brands
      };
    });
    
    return { regions, months };
  }
  
  /**
   * Get unique brands from regional data
   * @returns {Promise<Array>} List of unique brands
   */
  async getUniqueBrands() {
    try {
      const data = await this.getCampaignRegionalBreakdown();
      const brands = new Set();
      
      if (data?.data) {
        data.data.forEach(item => {
          if (item.brand_name) {
            brands.add(item.brand_name);
          }
        });
      }
      
      return Array.from(brands).sort();
    } catch (error) {
      console.error('❌ Error fetching unique brands:', error);
      return [];
    }
  }
  
  /**
   * Get unique campaigns from regional data
   * @param {string} brand - Optional brand filter
   * @returns {Promise<Array>} List of unique campaigns
   */
  async getUniqueCampaigns(brand = null) {
    try {
      const filters = brand ? { brand } : {};
      const data = await this.getCampaignRegionalBreakdown(filters);
      const campaigns = new Set();
      
      if (data?.data) {
        data.data.forEach(item => {
          if (item.campaign_name) {
            campaigns.add(item.campaign_name);
          }
        });
      }
      
      return Array.from(campaigns).sort();
    } catch (error) {
      console.error('❌ Error fetching unique campaigns:', error);
      return [];
    }
  }
}

// Export singleton instance
export default new RegionalDataService(); 